import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import QAHeroVisual from "@/components/QAHeroVisual";
import CTASection from "@/components/CTASection";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ServiceCard } from "@/components/ServiceCard";
import { CaseStudyCard } from "@/components/CaseStudyCard";
import { VALUE_CARDS, SERVICES, PROCESS_STEPS, MANIFESTO, CASE_STUDIES } from "@/data/content";

export default function Home() {
  return (
    <>
      <Seo
        title="Avirova Technologies — QA & Software Testing Services for Startups & SaaS"
        description="Professional QA and software testing services for startups, SaaS companies, and growing technology businesses. Manual, regression, API, web and mobile testing — your QA team, without the overhead."
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-28" data-testid="home-hero">
        <div className="grid-lines absolute inset-0" aria-hidden="true" />
        <div
          className="absolute -top-32 right-0 h-96 w-[36rem] rounded-full bg-volt/[0.05] blur-[140px]"
          aria-hidden="true"
        />
        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <MaskedLine delay={0.1}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
                {"// QA & Software Testing Services"}
              </p>
            </MaskedLine>
            <h1 className="mt-6 font-display text-[13.5vw] font-black leading-[0.95] tracking-tighter text-zinc-100 sm:text-7xl lg:text-[5.4rem]">
              <MaskedLine delay={0.2}>Ship with</MaskedLine>
              <MaskedLine delay={0.3}>confidence.</MaskedLine>
              <MaskedLine delay={0.4}>We make sure</MaskedLine>
              <MaskedLine delay={0.5}>your software</MaskedLine>
              <MaskedLine delay={0.6}>
                <span className="text-volt">works.</span>
              </MaskedLine>
            </h1>
            <MaskedLine delay={0.6}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
                Professional QA and software testing services for startups, SaaS
                companies, and growing technology businesses. From functional
                testing and regression to API and automation testing, we help
                you release reliable products faster.
              </p>
            </MaskedLine>
            <MaskedLine delay={0.88}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-volt px-7 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
                >
                  Get a Free QA Consultation
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-secondary-cta"
                  className="group inline-flex items-center gap-2 rounded-full border border-zinc-700 px-7 py-3.5 text-sm font-semibold text-zinc-200 transition-colors duration-300 hover:border-volt/50 hover:text-volt"
                >
                  Explore Services
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </MaskedLine>
            <MaskedLine delay={1}>
              <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600">
                Functional · Regression · API · Web · Mobile · UAT
              </p>
            </MaskedLine>
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <QAHeroVisual />
          </div>
        </div>
      </section>

      <Marquee />

      {/* VALUE */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="value-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader
              overline="Why QA matters"
              title={<>Quality shouldn't be the <span className="text-volt">bottleneck.</span></>}
              copy="Your development team should focus on building great products. We focus on making sure those products work the way they should."
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
            {VALUE_CARDS.map((card, i) => {
              const Icon = card.icon;
              return (
                <Reveal key={card.title} delay={i * 0.08}>
                  <div
                    data-testid={`value-card-${i}`}
                    className="group h-full rounded-2xl border border-zinc-800 bg-ink-900/60 p-7 transition-colors duration-500 hover:border-volt/40"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-zinc-800 bg-ink-950 text-zinc-300 transition-colors duration-500 group-hover:text-volt">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-zinc-100">
                      {card.title}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-zinc-400">{card.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-t border-zinc-800/80" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader
              overline="What we do"
              title="End-to-end QA testing services"
              copy="From a single release to continuous outsourced QA — pick the coverage your product needs."
            />
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="services-view-all-link"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-volt"
              >
                All Services
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-t border-zinc-800/80" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHeader
            overline="How we work"
            title="A simple QA process"
            copy="No ceremony for the sake of it. Four steps, repeated every cycle."
          />
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`process-step-${step.num}`}
                  className="group flex h-full flex-col bg-ink-950 p-8 transition-colors duration-500 hover:bg-ink-900"
                >
                  <span className="font-mono text-sm text-volt">{step.num} //</span>
                  <h3 className="mt-8 font-display text-2xl font-bold tracking-tight text-zinc-100">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO / WHY US */}
      <section className="border-t border-zinc-800/80" data-testid="why-us-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHeader
            overline="Why choose us"
            title={
              <>
                Built around your product.{" "}
                <span className="text-zinc-500">Not a one-size-fits-all process.</span>
              </>
            }
          />
          <div className="mt-16 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {MANIFESTO.map((item, i) => (
              <Reveal key={item.title} delay={(i % 4) * 0.08}>
                <div data-testid={`manifesto-item-${i}`} className="group border-t border-zinc-800 pt-6 transition-colors duration-500 hover:border-volt/50">
                  <span className="font-mono text-xs text-volt">
                    {String(i + 1).padStart(2, "0")} //
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-zinc-100">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDY PREVIEW */}
      <section className="border-t border-zinc-800/80" data-testid="case-study-preview-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader
              overline="Proof of process"
              title="See how structured QA can improve product quality"
              copy="Sample engagements showing our testing approach. Real client results will be published here as they're verified."
            />
            <Reveal delay={0.2}>
              <Link
                to="/case-studies"
                data-testid="view-case-studies-link"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-volt"
              >
                View Case Studies
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {CASE_STUDIES.map((study, i) => (
              <CaseStudyCard key={study.id} study={study} index={i} />
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
